/**
 * Unified Wallet API Client
 *
 * Real, Demo, Copy, Bot 모든 지갑을 한 번에 조회하는 API
 *
 * ⚠️ IMPORTANT: 신규 API (port 4333) 호출을 위해 apiClient 사용
 *
 * @see coin-server/apps/api/src/wallet/handlers/unified-wallet.handler.ts
 */

import apiClient from '@utils/apiClient';

/**
 * 단일 지갑 정보
 */
export interface WalletInfo {
  balance: string;
  lockbalance: string;
  availableBalance?: string;
  eventBalance?: string;
}

/**
 * Real 지갑 (Spot + Futures)
 */
export interface RealWallets {
  spot: WalletInfo;
  futures: WalletInfo;
}

/**
 * 통합 지갑 응답
 */
export interface UnifiedWalletResponse {
  success: boolean;
  data: {
    real: RealWallets;
    demo: WalletInfo;
    copy: WalletInfo;
    bot: WalletInfo;
  };
}

/**
 * 통합 지갑 조회
 *
 * GET /v1/wallet/unified
 *
 * @returns Real, Demo, Copy, Bot 모든 지갑 정보
 *
 * @example
 * ```typescript
 * const { data } = await getUnifiedWallets();
 *
 * console.log('Real Spot:', data.real.spot.balance);
 * console.log('Real Futures:', data.real.futures.balance);
 * console.log('Demo:', data.demo.balance);
 * console.log('Copy:', data.copy.balance);
 * console.log('Bot:', data.bot.balance);
 * ```
 */
export const getUnifiedWallets = async (): Promise<UnifiedWalletResponse> => {
  // apiClient는 baseURL이 NEW_API_URL (port 4333)로 설정되어 있음
  // 요청 URL: {NEW_API_URL}/wallet/unified
  const response = await apiClient.get<UnifiedWalletResponse>('/wallet/unified');

  return response.data;
};

/**
 * Unified Wallet API 헬퍼
 */
export const unifiedWalletApi = {
  /**
   * 통합 지갑 조회
   */
  async getAll(): Promise<UnifiedWalletResponse> {
    return getUnifiedWallets();
  },

  /**
   * Real 지갑만 조회
   */
  async getRealWallets(): Promise<RealWallets> {
    const response = await getUnifiedWallets();
    return response.data.real;
  },

  /**
   * Demo 지갑만 조회
   */
  async getDemoWallet(): Promise<WalletInfo> {
    const response = await getUnifiedWallets();
    return response.data.demo;
  },

  /**
   * Copy 지갑만 조회
   */
  async getCopyWallet(): Promise<WalletInfo> {
    const response = await getUnifiedWallets();
    return response.data.copy;
  },

  /**
   * Bot 지갑만 조회
   */
  async getBotWallet(): Promise<WalletInfo> {
    const response = await getUnifiedWallets();
    return response.data.bot;
  },
};
